import {
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItemRadio,
  MenuDivider,
  makeStyles,
  tokens,
  mergeClasses,
} from '@fluentui/react-components';
import { useOrgContext } from '../../providers/DataverseOrgProvider';
import { veracityColors } from '../tokens';

const useStyles = makeStyles({
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '2px 10px',
    borderRadius: '12px',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    userSelect: 'none',
    fontFamily: 'inherit',
  },
  // Button-shaped (when interactive) — reset native button chrome
  badgeButton: {
    cursor: 'pointer',
    transitionProperty: 'transform, opacity',
    transitionDuration: '120ms',
    ':hover': {
      opacity: 0.85,
    },
  },
  prod: {
    backgroundColor: 'rgba(34, 139, 80, 0.12)',
    color: '#1a6e42',
    border: '1px solid rgba(34, 139, 80, 0.3)',
  },
  dev: {
    backgroundColor: 'rgba(214, 70, 29, 0.12)',
    color: veracityColors.veracityOrange,
    border: '1px solid rgba(214, 70, 29, 0.4)',
  },
  onDark: {
    backgroundColor: 'rgba(255,255,255,0.1)',
    color: '#FFFFFF',
    border: '1px solid rgba(255,255,255,0.25)',
  },
  dot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  dotProd: { backgroundColor: '#22a06b' },
  dotDev: { backgroundColor: veracityColors.veracityOrange },
  footer: {
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
    fontSize: tokens.fontSizeBase100,
    color: veracityColors.duskBlue,
  },
});

interface EnvBadgeProps {
  /** Override variant — `on-dark` for dark headers (AppShell). */
  variant?: 'auto' | 'on-dark';
}

/**
 * Reactive environment indicator + switcher. The badge is a Menu
 * trigger; clicking it lets the user switch between Production and
 * Development Dataverse. Hidden entirely on production-only builds
 * (no dev org configured).
 *
 * The data-env switch is independent of which app build the user is
 * on — a prod-deployed shell wouldn't have a dev org configured at
 * all, so the badge naturally disappears there.
 */
export function EnvBadge({ variant = 'auto' }: EnvBadgeProps) {
  const styles = useStyles();
  const { activeOrg, setActiveOrg, hasDevOrg } = useOrgContext();

  if (!hasDevOrg) return null;

  const isProd = activeOrg === 'prod';
  const label = isProd ? 'Prod' : 'Dev';
  const colorClass = variant === 'on-dark' ? styles.onDark : isProd ? styles.prod : styles.dev;
  const dotClass = mergeClasses(styles.dot, isProd ? styles.dotProd : styles.dotDev);

  const inner = (
    <>
      <span className={dotClass} />
      {label}
    </>
  );

  return (
    <Menu checkedValues={{ env: [activeOrg] }}>
      <MenuTrigger>
        <button
          type="button"
          className={mergeClasses(styles.badge, colorClass, styles.badgeButton)}
          title={`Click to switch environment (currently ${label})`}
        >
          {inner}
        </button>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          <MenuItemRadio name="env" value="prod" onClick={() => setActiveOrg('prod')}>
            Production
          </MenuItemRadio>
          <MenuItemRadio name="env" value="dev" onClick={() => setActiveOrg('dev')}>
            Development
          </MenuItemRadio>
          <MenuDivider />
          <div className={styles.footer}>Switching reloads the page.</div>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}
